"use client";

import { DatabaseIcon, HashIcon, PanelsTopLeftIcon } from "lucide-react";
import {
  forwardRef,
  useId,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
  type ForwardedRef,
  type KeyboardEvent,
  type UIEvent,
} from "react";

import { FileGlyph } from "@/components/content/icons/file-type-icons";
import { Textarea, textareaClassName } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

export type MentionEntityType = "channel" | "dataSet" | "teammate" | "view" | "file";

export const MENTION_ENTITY_STYLES: Record<
  MentionEntityType,
  { inline: string }
> = {
  file: {
    inline: "bg-primary/10 text-primary",
  },
  teammate: {
    inline: "bg-primary/10 text-primary",
  },
  channel: {
    inline: "bg-badge-violet/10 text-badge-violet-foreground",
  },
  dataSet: {
    inline: "bg-badge-amber/10 text-badge-amber-foreground",
  },
  view: {
    inline: "bg-badge-teal/10 text-badge-teal-foreground",
  },
};

export type MentionEntity = {
  id: string;
  type: MentionEntityType;
  label: string;
  description?: string;
  imageUrl?: string | null;
};

type MentionQuery = { start: number; end: number; value: string };

type EntityMentionInputProps = Omit<
  ComponentProps<typeof Textarea>,
  "onChange" | "onKeyDown" | "value"
> & {
  entities: MentionEntity[];
  listboxLabel: string;
  menuPlacement?: "above" | "below";
  onKeyDown?: (event: KeyboardEvent<HTMLTextAreaElement>) => void;
  onMention: (entity: MentionEntity, insertedValue: string) => string | void;
  /** Told when a mention starts or ends, so candidates can load on demand. */
  onMentionQuery?: (active: boolean) => void;
  onValueChange: (value: string) => void;
  value: string;
};

export const EntityMentionInput = forwardRef<
  HTMLTextAreaElement,
  EntityMentionInputProps
>(function EntityMentionInput(
  {
    className,
    entities,
    listboxLabel,
    menuPlacement = "above",
    onFocus,
    onKeyDown,
    onMention,
    onMentionQuery,
    onScroll,
    onValueChange,
    style,
    value,
    ...textareaProps
  },
  forwardedRef,
) {
  const listboxId = useId();
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const [query, setQuery] = useState<MentionQuery | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const highlightSegments = useMemo(
    () => mentionHighlightSegments(value, entities),
    [entities, value],
  );
  const hasMentions = highlightSegments.some(
    (segment) => segment.kind === "mention",
  );
  const uniqueEntities = useMemo(() => dedupeMentionEntities(entities), [entities]);
  const matches = useMemo(
    () => (query ? rankMentionMatches(uniqueEntities, query.value) : []),
    [query, uniqueEntities],
  );

  const updateQuery = (nextValue: string, caret: number | null) => {
    const next = caret === null ? null : mentionQuery(nextValue, caret);
    setQuery(next);
    setActiveIndex(0);
    onMentionQuery?.(next !== null);
  };
  const selectEntity = (entity: MentionEntity) => {
    if (!query) return;
    const insertion = insertMention(value, query, entity.label);
    const mentionedValue = onMention(entity, insertion.value);
    onValueChange(mentionedValue ?? insertion.value);
    setQuery(null);
    requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.setSelectionRange(insertion.caret, insertion.caret);
    });
  };
  const menuOpen = Boolean(query && matches.length);
  const resolvedActiveIndex = activeIndex < matches.length ? activeIndex : 0;
  const syncOverlayScroll = (event: UIEvent<HTMLTextAreaElement>) => {
    const overlay = overlayRef.current;
    if (overlay) {
      overlay.scrollTop = event.currentTarget.scrollTop;
      overlay.scrollLeft = event.currentTarget.scrollLeft;
    }
    onScroll?.(event);
  };

  return (
    <div className={cn("relative", hasMentions && "bg-control")}>
      {hasMentions ? (
        <div
          ref={overlayRef}
          aria-hidden
          style={style}
          className={cn(
            textareaClassName,
            className,
            "pointer-events-none absolute inset-0 z-0 block overflow-auto whitespace-pre-wrap break-words bg-transparent shadow-none ring-0 hover:bg-transparent",
          )}
        >
          {highlightSegments.map((segment, index) =>
            segment.kind === "mention" ? (
              <span
                key={index}
                title={segment.title}
                className={MENTION_ENTITY_STYLES[segment.entityType].inline}
              >
                {segment.text}
              </span>
            ) : (
              <span key={index}>{segment.text}</span>
            ),
          )}
          {"\n"}
        </div>
      ) : null}
      <Textarea
        {...textareaProps}
        style={style}
        className={cn(
          className,
          hasMentions &&
            "relative z-[1] bg-transparent caret-foreground text-transparent [-webkit-text-fill-color:transparent]",
        )}
        ref={(input) => {
          inputRef.current = input;
          assignRef(forwardedRef, input);
        }}
        value={value}
        onScroll={syncOverlayScroll}
        aria-autocomplete="list"
        aria-controls={menuOpen ? listboxId : undefined}
        aria-expanded={menuOpen}
        aria-activedescendant={
          menuOpen ? `${listboxId}-${resolvedActiveIndex}` : undefined
        }
        onFocus={(event) => {
          onFocus?.(event);
          updateQuery(value, event.currentTarget.selectionStart);
        }}
        onChange={(event) => {
          onValueChange(event.target.value);
          updateQuery(event.target.value, event.target.selectionStart);
        }}
        onClick={(event) =>
          updateQuery(value, event.currentTarget.selectionStart)
        }
        onKeyDown={(event) => {
          if (event.nativeEvent.isComposing) {
            onKeyDown?.(event);
            return;
          }
          if (query && event.key === "Escape") {
            event.preventDefault();
            setQuery(null);
            return;
          }
          if (query && matches.length) {
            if (event.key === "ArrowDown" || event.key === "ArrowUp") {
              event.preventDefault();
              setActiveIndex(
                (current) =>
                  (current +
                    (event.key === "ArrowDown" ? 1 : matches.length - 1)) %
                  matches.length,
              );
              return;
            }
            if (event.key === "Enter" || event.key === "Tab") {
              event.preventDefault();
              selectEntity(matches[resolvedActiveIndex] ?? matches[0]!);
              return;
            }
          }
          onKeyDown?.(event);
        }}
      />
      {menuOpen ? (
        <div
          id={listboxId}
          role="listbox"
          aria-label={listboxLabel}
          className={cn(
            "absolute inset-x-0 z-(--z-overlay) overflow-hidden rounded-lg border border-border bg-popover p-1 shadow-lg",
            menuPlacement === "above" ? "bottom-full mb-1" : "top-full mt-1",
          )}
        >
          {matches.map((entity, index) => (
            <button
              id={`${listboxId}-${index}`}
              key={`${entity.type}:${entity.id}`}
              type="button"
              role="option"
              aria-selected={index === resolvedActiveIndex}
              className={cn(
                "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm",
                index === resolvedActiveIndex
                  ? "bg-muted"
                  : "hover:bg-muted/70",
              )}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => selectEntity(entity)}
            >
              <MentionEntityIcon entity={entity} />
              <span className="min-w-0 flex-1">
                <span className="block truncate font-medium">
                  {entity.label}
                </span>
                {entity.description ? (
                  <span className="block truncate text-xs text-muted-foreground">
                    {entity.description}
                  </span>
                ) : null}
              </span>
              <span className="shrink-0 text-3xs text-muted-foreground">
                {mentionTypeLabel(entity.type)}
              </span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
});

function MentionEntityIcon({ entity }: { entity: MentionEntity }) {
  const [imageFailed, setImageFailed] = useState(false);
  if (entity.type === "teammate") {
    return (
      <span className="flex size-7 shrink-0 items-center justify-center overflow-hidden rounded-full border border-border/70 bg-muted text-3xs font-semibold text-muted-foreground">
        {entity.imageUrl && !imageFailed ? (
          <img
            src={entity.imageUrl}
            alt=""
            className="size-full object-cover"
            referrerPolicy="no-referrer"
            onError={() => setImageFailed(true)}
          />
        ) : (
          initials(entity.label)
        )}
      </span>
    );
  }
  if (entity.type === "file") {
    return (
      <span className="flex size-7 shrink-0 items-center justify-center">
        <FileGlyph file={{ fileName: entity.label }} className="size-5" />
      </span>
    );
  }
  const Icon =
    entity.type === "channel"
      ? HashIcon
      : entity.type === "dataSet"
        ? DatabaseIcon
        : PanelsTopLeftIcon;
  return (
    <span className="flex size-7 shrink-0 items-center justify-center">
      <Icon className="size-4 text-muted-foreground" aria-hidden />
    </span>
  );
}

function mentionTypeLabel(type: MentionEntity["type"]): string {
  if (type === "file") return "File";
  if (type === "teammate") return "User";
  if (type === "dataSet") return "Data Set";
  return type === "channel" ? "Channel" : "View";
}

export type MentionHighlightSegment =
  | { kind: "text"; text: string }
  | { kind: "mention"; text: string; entityType: MentionEntityType; title?: string };

export function mentionHighlightSegments(
  value: string,
  entities: readonly MentionEntity[],
): MentionHighlightSegment[] {
  const byLabel = new Map<string, MentionEntity>();
  for (const entity of entities) {
    const label = entity.label.trim();
    if (label && !byLabel.has(label)) byLabel.set(label, entity);
  }
  const labels = [...byLabel.keys()].sort(
    (left, right) => right.length - left.length || left.localeCompare(right),
  );
  if (!value || labels.length === 0) {
    return value ? [{ kind: "text", text: value }] : [];
  }
  const pattern = new RegExp(
    `(^|[\\s(])@(${labels.map(escapeRegExp).join("|")})(?=[\\s),.!?]|$)`,
    "gu",
  );
  const segments: MentionHighlightSegment[] = [];
  let cursor = 0;
  for (const match of value.matchAll(pattern)) {
    const prefix = match[1] ?? "";
    const label = match[2] ?? "";
    const mentionStart = (match.index ?? 0) + prefix.length;
    if (mentionStart > cursor) {
      segments.push({ kind: "text", text: value.slice(cursor, mentionStart) });
    }
    segments.push({
      kind: "mention",
      text: `@${label}`,
      entityType: byLabel.get(label)?.type ?? "channel",
      ...(byLabel.get(label)?.type === "file" ? { title: byLabel.get(label)?.description } : {}),
    });
    cursor = mentionStart + label.length + 1;
  }
  if (cursor < value.length)
    segments.push({ kind: "text", text: value.slice(cursor) });
  return segments;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/gu, "\\$&");
}

export function mentionQuery(
  value: string,
  caret: number,
): MentionQuery | null {
  const beforeCaret = value.slice(0, caret);
  const match = /(?:^|[\s(])@([^@\n]{0,50})$/u.exec(beforeCaret);
  if (!match) return null;
  const queryValue = match[1] ?? "";
  return {
    start: caret - queryValue.length - 1,
    end: caret,
    value: queryValue.trimStart(),
  };
}

export function insertMention(
  value: string,
  query: MentionQuery,
  label: string,
) {
  const suffix = value.slice(query.end);
  const separator = suffix.startsWith(" ") ? "" : " ";
  const nextValue = `${value.slice(0, query.start)}@${label}${separator}${suffix}`;
  return { value: nextValue, caret: query.start + label.length + 2 };
}

/**
 * The entities a mention query names, best first: the label's start, then a
 * word in the label, then anywhere in the label, then the description. A
 * description that merely mentions a name never outranks the name itself.
 */
export function rankMentionMatches(
  entities: readonly MentionEntity[],
  query: string,
  limit = 6,
): MentionEntity[] {
  const search = query.toLocaleLowerCase();
  const rank = (entity: MentionEntity) => {
    const label = entity.label.toLocaleLowerCase();
    if (label.startsWith(search)) return 0;
    const at = label.indexOf(search);
    if (at > 0) return /[\p{L}\p{N}]/u.test(label[at - 1]!) ? 2 : 1;
    return entity.description?.toLocaleLowerCase().includes(search) ? 3 : null;
  };
  return entities
    .map((entity, index) => ({ entity, index, rank: rank(entity) }))
    .filter((match): match is typeof match & { rank: number } => match.rank !== null)
    .sort((left, right) => left.rank - right.rank || left.index - right.index)
    .slice(0, limit)
    .map((match) => match.entity);
}

function dedupeMentionEntities(entities: MentionEntity[]): MentionEntity[] {
  return [
    ...new Map(
      entities.map((entity) => [`${entity.type}:${entity.id}`, entity]),
    ).values(),
  ];
}

function assignRef(
  ref: ForwardedRef<HTMLTextAreaElement>,
  input: HTMLTextAreaElement | null,
) {
  if (typeof ref === "function") {
    ref(input);
  } else if (ref) {
    ref.current = input;
  }
}

function initials(value: string): string {
  return (
    value
      .trim()
      .split(/\s+/u)
      .slice(0, 2)
      .map((part) => part[0]?.toLocaleUpperCase() ?? "")
      .join("") || "?"
  );
}
